import {
  DrawerBody,
  DrawerFooter,
  DrawerContent,
  Drawer,
  DrawerHeader,
  Button,
  Tabs,
  Tab,
  addToast,
  Spinner,
} from "@heroui/react";
import { Trans } from "@lingui/react/macro";
import { t } from "@lingui/core/macro";
import { useForm } from "@mantine/form";
import { api } from "@/lib/api-client";
import { Language, GetSubscription } from "@/server-types";
import { SUPPORTED_LOCALES } from "../../../../../shared/constants/locales";
import usePreferredLanguageStore from "@/stores/usePreferredLanguageStore";
import { FormValues } from "./types";
import {
  GeneralTab,
  ServicesTab,
  VariationsTab,
  ConfirmationTab,
} from "./components/tabs";
import { useEffect, useState } from "react";
import { useMediaQuery } from "@mantine/hooks";
import { useSearchParams } from "react-router";
const SUBSCRIPTION_COLORS = [
  "#E9B2BF",
  "#EFE8CB",
  "#84D39C",
  "#DAE1F1",
  "#FFD964",
  "#B2DFE9",
];
function getRandomColor() {
  return SUBSCRIPTION_COLORS[
    Math.floor(Math.random() * SUBSCRIPTION_COLORS.length)
  ];
}

const getInitialFormValues = (
  subscription?: GetSubscription["response"],
): Omit<FormValues, "currentTab"> => {
  return {
    ...(subscription
      ? {
          ...subscription,
          tagIds: subscription.tags.map((t) => t.id),
          subscriptionServices: subscription.subscriptionServices.map((s) => ({
            ...s,
            limitEntries: s.limitations.length > 0,
          })),
          subscriptionVariations: subscription.subscriptionVariations.map(
            (v) => ({
              ...v,
              limitEntries: v.limitations.length > 0,
              id: v.id,
              priceCents: v.priceCents / 100,
            }),
          ),
        }
      : {
          nameLocalized: SUPPORTED_LOCALES.map((language: Language) => ({
            language,
            value: "",
          })),
          descriptionLocalized: SUPPORTED_LOCALES.map((language: Language) => ({
            language,
            value: "",
          })),
          isActive: true,
          subscriptionVariations: [
            {
              nameLocalized: SUPPORTED_LOCALES.map((language: Language) => ({
                language,
                value: "",
              })),
              durationValue: 1,
              durationUnit: "month",
              priceCents: 0,
              isRecurring: false,
              limitEntries: false,
              limitations: [],
            },
          ],
          subscriptionServices: [],
          tagIds: [],
          color: getRandomColor(),
          categoryId: null,
        }),
    updateCustomerSubscriptions: false,
  };
};

const SubscriptionFormDrawer = ({
  isOpen,
  onSuccess,
  subscriptionId,
  initialTab,
}: {
  isOpen: boolean;
  onSuccess: () => void;
  subscriptionId?: number;
  initialTab?: "general" | "services" | "variations" | "confirmation";
}) => {
  const [_, setSearchParams] = useSearchParams();

  const isEditingSubscription = !!subscriptionId && subscriptionId !== -1;
  const isMobile = useMediaQuery("(max-width: 768px)");
  const { preferredLanguage } = usePreferredLanguageStore((state) => state);
  const defaultLanguage =
    preferredLanguage.userPreferredLanguage ||
    preferredLanguage.clientPreferredLanguage ||
    "sl";

  const [originalSubscriptionsHash, setOriginalSubscriptionsHash] = useState<
    string | null
  >(null);
  const { data: subscription, isLoading } = api.subscription.useGetSubscription(
    subscriptionId ?? -1,
  );

  const { mutateAsync: createSubscription } =
    api.subscription.usePostSubscription();
  const { mutateAsync: updateSubscription } =
    api.subscription.usePutSubscription();

  const form = useForm<FormValues>({
    initialValues: getInitialFormValues(),
    transformValues: (values) => ({
      ...values,
      subscriptionVariations: values.subscriptionVariations.map(
        (variation) => ({
          ...variation,
          priceCents: Math.round(variation.priceCents * 100),
        }),
      ),
    }),
    validate: {
      nameLocalized: (values) => {
        const hasNonEmptyName = values.some((v) => v.value.trim() !== "");
        return hasNonEmptyName ? null : "Vnesi ime naročnine";
      },
      subscriptionVariations: {
        nameLocalized: (variationNameLocalized) => {
          const hasNonEmptyVariationName = variationNameLocalized.some(
            (n) => n.value.trim() !== "",
          );
          return hasNonEmptyVariationName
            ? null
            : t`Ime variacije ne sme biti prazno`;
        },
        durationValue: (value) =>
          value <= 0 ? t`Trajanje mora biti vsaj 1` : null,
        priceCents: (value) =>
          value < 0 ? t`Cena ne sme biti negativna` : null,
      },
    },
  });

  useEffect(() => {
    if (!isOpen) {
      form.reset();
    } else {
      form.setFieldValue("currentTab", initialTab ?? "general");
    }
  }, [isOpen]);

  useEffect(() => {
    if (subscription) {
      const initialValues = getInitialFormValues(subscription);
      form.setValues(initialValues);
      setOriginalSubscriptionsHash(
        JSON.stringify(initialValues.subscriptionVariations),
      );
    }
  }, [subscription]);

  const handleFormSubmit = async (values: FormValues) => {
    const { currentTab, ...formValues } = values;

    try {
      if (isEditingSubscription) {
        await updateSubscription({
          subscriptionId,
          body: formValues,
        });
        addToast({
          color: "success",
          title: t`Uspešno`,
          description: t`Naročnina je bila uspešno posodobljena`,
        });
      } else {
        await createSubscription(formValues);
        addToast({
          color: "success",
          title: t`Uspešno`,
          description: t`Naročnina je bila uspešno ustvarjena`,
        });
      }

      onSuccess();
    } catch (error) {
      addToast({
        color: "danger",
        title: t`Napaka`,
        description: t`Prišlo je do napake pri ${
          isEditingSubscription ? "posodabljanju" : "ustvarjanju"
        } naročnine`,
      });
    }
  };

  const handleValidationError = (
    validationErrors: Record<string, React.ReactNode>,
  ) => {
    addToast({
      color: "danger",
      title: t`Napaka`,
      description: String(Object.values(validationErrors)[0]),
    });
  };

  if (isLoading && isEditingSubscription) {
    return (
      <Drawer
        isOpen={isOpen}
        isDismissable={true}
        isKeyboardDismissDisabled={false}
      >
        <DrawerContent className="mt-5">
          <DrawerHeader>
            <div className="flex flex-col items-center justify-center">
              <Spinner />
            </div>
          </DrawerHeader>
        </DrawerContent>
      </Drawer>
    );
  }

  const formValues = form.getValues();

  return (
    <Drawer
      isOpen={isOpen}
      isDismissable={true}
      isKeyboardDismissDisabled={false}
      placement={isMobile ? "bottom" : "right"}
      size={isMobile ? "5xl" : "md"}
      onClose={() => {
        setSearchParams((prev) => {
          prev.delete("sid");
          return prev;
        });
      }}
    >
      <form onSubmit={form.onSubmit(handleFormSubmit, handleValidationError)}>
        <DrawerContent>
          {(onClose) => (
            <>
              <DrawerHeader>
                <div className="flex flex-col">
                  {isEditingSubscription ? (
                    <Trans>Uredi naročnino</Trans>
                  ) : (
                    <Trans>Ustvari novo naročnino</Trans>
                  )}
                  <p className="text-sm font-normal text-gray-500">
                    {formValues.currentTab === "general" && (
                      <Trans>Splošno</Trans>
                    )}
                    {formValues.currentTab === "services" && (
                      <Trans>Storitve</Trans>
                    )}
                    {formValues.currentTab === "variations" && (
                      <Trans>Variacije</Trans>
                    )}
                    {formValues.currentTab === "confirmation" && (
                      <Trans>Pregled</Trans>
                    )}
                  </p>
                </div>
              </DrawerHeader>
              <DrawerBody>
                <div>
                  {formValues.currentTab === "confirmation" ? (
                    <ConfirmationTab
                      form={form}
                      hasChangedVariations={
                        originalSubscriptionsHash !==
                        JSON.stringify(form.values.subscriptionVariations)
                      }
                    />
                  ) : (
                    <Tabs
                      classNames={{
                        tabList: "bg-white",
                        tab: "border-1 border-gray-200 data-[selected=true]:border-0",
                        cursor:
                          " group-data-[selected=true]:bg-primary/30 shadow-none ",
                      }}
                      variant="light"
                      fullWidth={true}
                      disableAnimation
                      selectedKey={formValues.currentTab}
                      onSelectionChange={(key) => {
                        form.setFieldValue(
                          "currentTab",
                          key?.toString() ?? "general",
                        );
                      }}
                    >
                      <Tab key="general" title="Splošno">
                        <GeneralTab
                          form={form}
                          defaultLanguage={defaultLanguage}
                        />
                      </Tab>

                      <Tab key="services" title="Storitve">
                        <ServicesTab form={form} />
                      </Tab>

                      <Tab key="variations" title="Variacije">
                        <VariationsTab form={form} />
                      </Tab>
                    </Tabs>
                  )}
                </div>
              </DrawerBody>

              <DrawerFooter>
                <Button
                  variant="bordered"
                  type="button"
                  onPress={() => {
                    if (formValues.currentTab === "general") {
                      onClose();
                    } else if (formValues.currentTab === "services") {
                      form.setFieldValue("currentTab", "general");
                    } else if (formValues.currentTab === "variations") {
                      form.setFieldValue("currentTab", "services");
                    } else if (formValues.currentTab === "confirmation") {
                      form.setFieldValue("currentTab", "variations");
                    }
                  }}
                >
                  {formValues.currentTab === "general" ? (
                    <Trans>Prekliči</Trans>
                  ) : (
                    <Trans>Nazaj</Trans>
                  )}
                </Button>
                <Button
                  key={formValues.currentTab}
                  type={
                    formValues.currentTab === "confirmation"
                      ? "submit"
                      : "button"
                  }
                  color="primary"
                  data-identifier="subscription-form-submit"
                  onPress={() => {
                    if (formValues.currentTab === "general") {
                      if (form.validateField("nameLocalized").hasError) {
                        return;
                      }
                      form.setFieldValue("currentTab", "services");
                    } else if (formValues.currentTab === "services") {
                      form.setFieldValue("currentTab", "variations");
                    } else if (formValues.currentTab === "variations") {
                      form.validate();
                      if (form.validate().hasErrors) {
                        return;
                      }
                      form.setFieldValue("currentTab", "confirmation");
                    }
                  }}
                >
                  {formValues.currentTab === "confirmation" ? (
                    isEditingSubscription ? (
                      <Trans>Shrani</Trans>
                    ) : (
                      <Trans>Ustvari</Trans>
                    )
                  ) : (
                    <Trans>Nadaljuj</Trans>
                  )}
                </Button>
              </DrawerFooter>
            </>
          )}
        </DrawerContent>
      </form>
    </Drawer>
  );
};

export default SubscriptionFormDrawer;
